/**
 * Provider Evaluation Required block.
 *
 * Compliance-critical component that appears above the fold on every
 * treatment / peptide / condition page. Communicates that no therapy is
 * dispensed without licensed medical provider review. Uses in-person /
 * telemedicine-agnostic language per stakeholder direction (2026-07-23):
 *
 *   "Meet with one of our licensed medical providers to determine whether
 *    peptide therapy is appropriate for you."
 *
 * This language works whether the visit is in-clinic or via telemedicine.
 */
import { ShieldCheck, Stethoscope, ClipboardList } from "lucide-react";
import { ScheduleConsultationCTA } from "./ScheduleConsultationCTA";

const DEFAULT_ITEMS = [
  { icon: Stethoscope, label: "Licensed medical provider evaluation" },
  { icon: ClipboardList, label: "Individualized treatment plan" },
  { icon: ShieldCheck, label: "Ongoing clinical support" },
];

export function ProviderEvaluationRequired({
  location = "provider-evaluation-block",
  items = DEFAULT_ITEMS,
  title = "Provider Evaluation Required",
  body = "Meet with one of our licensed medical providers to determine whether peptide therapy is appropriate for you. If clinically appropriate, we'll create a personalized treatment plan and coordinate your care through appropriate pharmacy channels in accordance with applicable laws and regulations.",
  compact = false,
}) {
  return (
    <div
      data-testid="provider-evaluation-required"
      className={[
        "rounded-2xl border border-[color:var(--vv-teal)]/40 bg-gradient-to-br from-teal-50/60 to-sky-50/40",
        compact ? "p-4" : "p-6 md:p-8",
      ].join(" ")}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[color:var(--vv-teal)] font-semibold mb-2">
        <ShieldCheck size={14} />
        {title}
      </div>
      <p className={compact ? "text-sm text-stone-700" : "text-stone-800 md:text-lg leading-relaxed"}>
        {body}
      </p>
      <ul className={`grid gap-2 mt-4 ${compact ? "" : "sm:grid-cols-3"}`}>
        {items.map((it, i) => {
          const Icon = it.icon || Stethoscope;
          return (
            <li key={it.label || `item-${i}`} className="flex items-center gap-2 text-sm text-stone-700">
              <span className="w-8 h-8 rounded-full bg-white border border-[color:var(--vv-teal)]/30 flex items-center justify-center text-[color:var(--vv-teal)] shrink-0">
                <Icon size={16} />
              </span>
              {it.label}
            </li>
          );
        })}
      </ul>
      <div className="mt-5">
        <ScheduleConsultationCTA location={location} size="lg" />
      </div>
    </div>
  );
}

export default ProviderEvaluationRequired;
